import AuthSvcMod from '@Services/Auth.svc.js';
import ConsultationSvcMod from '@Services/Consultation.svc.js';
import FeatureFlagsSvcMod from '@Services/FeatureFlags.svc.js';
import MetricsSvcMod from '@Services/Metrics.svc.js';
import NavSvcMod from '@Services/Nav.svc.js';
import StorageSvcMod from '@Services/Storage.svc.js';
/** @typedef {import('./RoutesResolve.svc')} Types */
/**
 * Window in which a presentation flag lookup is reused. Long enough for `lazyLoad` and the
 * `providerFlags` resolve of one transition to share a request, short enough that ordinary
 * navigation still sees fresh flags. Mirrors the debounce in `AuthSvc.isAuth0Flow`.
 */
/** Single wording for the not-logged-in rejection, previously hand-written in four places. */
//
export class RoutesResolve {
	constructor(
		$http,
		$q,
		API,
		$log,
		dateFns,
		AuthSvc,
		MetricsSvc,
		NavSvc,
		StorageSvc,
		FeatureFlagsSvc,
		ConsultationService,
		PUBLIC_KEY
	) {
		'ngInject';
		this.$http = $http;
		this.$q = $q;
		this.api = API;
		this.$log = $log;
		this.dateFns = dateFns;
		this.AuthSvc = AuthSvc;
		this.NavSvc = NavSvc;
		this.MetricsSvc = MetricsSvc;
		this.StorageSvc = StorageSvc;
		this.FeatureFlagsSvc = FeatureFlagsSvc;
		this.ConsultationService = ConsultationService;
		this.PUBLIC_KEY = PUBLIC_KEY;
		this._presentationFlags = null;
		this.FLAG_CACHE_TTL_MS = 5000;
		this.NOT_LOGGED_IN = 'User must be logged in';
	}

	/**
	 * Builds the rejection shape used by every method here except the two consultation
	 * validators, which reject with a `{name, params}` redirect instruction instead.
	 *
	 * `display` is a human-readable description for logging — no route consumer renders it
	 * directly. `fromState` records which page the failure happened on, and `response`
	 * carries the raw server payload when it is the thing at fault. `data.error` is copied
	 * rather than mutated because it belongs to the live $http response.
	 *
	 * @param {Types.BuildRejectionParams} params
	 * @returns {Object} the rejection object
	 */
	buildRejection(params) {
		const {display, fromState, error, response} = params || {};
		const _rejection = Object.assign({}, error, {display, fromState: fromState || ''});
		if (response) _rejection.response = response;
		return _rejection;
	}

	/** @param {Types.GetFlagsCapabilitiesLogAccessParams} params */
	getFlagsCapabilitiesLogAccess(params) {
		if (!this.AuthSvc.hasValidUser()) {
			return this.$q.reject(this.buildRejection({display: this.NOT_LOGGED_IN, fromState: params?.fromState}));
		}

		const getFlags = this.getFlagsPrivate(params);
		const getCapabilities = this.getUserCapabilities(params);
		const logAccess = this.postLastUserAccess(params);
		return this.$q.all([getFlags, getCapabilities, logAccess]);
	}

	/** @param {Types.GetFlagsPrivateParams} params */
	getFlagsPrivate(params) {
		const _fromState = params?.fromState;
		if (!this.AuthSvc.hasValidUser()) {
			return this.$q.reject(this.buildRejection({display: this.NOT_LOGGED_IN, fromState: _fromState}));
		}
		const _user = this.AuthSvc.currentUser();

		const _header = {'X-State-Name': _fromState || ''};
		const _display = 'Error getting features flag';
		const _params = {serviceProviderId: _user?.service_provider_id, userId: _user?.id};
		return this.$http
			.get(`${this.api}/serviceProvider/featureflags`, {params: _params, headers: _header})
			.then(resObj => {
				const {data} = resObj;
				if (data?.success) {
					this.FeatureFlagsSvc.addFlags(data.payLoad);
					return data.payLoad;
				}
				if (data?.error) {
					return this.$q.reject(
						this.buildRejection({display: _display, fromState: _fromState, error: data.error})
					);
				}

				return this.$q.reject(
					this.buildRejection({display: _display, fromState: _fromState, response: data})
				);
			})
			.catch(resObj => {
				this.$log.error(resObj);
				return this.$q.reject(this.buildRejection({display: _display, fromState: _fromState}));
			});
	}

	/** @param {Types.GetFlagsPublicParams} params */
	getFlagsPublic(params) {
		const _fromState = params?.fromState;
		if (!params || !params?.serviceProviderId || !params?.consultationId || !params?.customerToken) {
			return this.$q.reject(this.buildRejection({display: 'Params are required', fromState: _fromState}));
		}

		const _header = {'X-State-Name': _fromState || '', Auth: this.PUBLIC_KEY};
		const _display = 'Error getting features flag public';
		const _params = {
			serviceProviderId: Number(params?.serviceProviderId),
			consultationId: Number(params?.consultationId),
			customerToken: params?.customerToken,
		};
		return (
			this.$http
				.get(`${this.api}/serviceProvider/featureFlagsForCustomer`, {params: _params, headers: _header})
				.then(resObj => {
					const {data} = resObj;
					if (data?.success) {
						this.FeatureFlagsSvc.addFlags(data.payLoad);
						return data.payLoad;
					}
					if (data?.error) {
						return this.$q.reject(
							this.buildRejection({display: _display, fromState: _fromState, error: data.error})
						);
					}

					return this.$q.reject(
						this.buildRejection({display: _display, fromState: _fromState, response: data})
					);
				})
				// resObj is not merged in: it carries config.headers, including the PUBLIC_KEY
				.catch(resObj => {
					this.$log.error(resObj);
					return this.$q.reject(this.buildRejection({display: _display, fromState: _fromState}));
				})
		);
	}

	/**
	 * Feature flags for a presentation route, shared by the state's `lazyLoad` and its
	 * `providerFlags` resolve.
	 *
	 * Both need the v2 flag — `lazyLoad` to import only the component that will actually
	 * render, `templateProvider` to render it. Reusing one promise across the pair is not
	 * just about saving a request: if the two read different values we would register one
	 * component and render the other, which fails silently as an empty view.
	 *
	 * Which endpoint to use is derived from the destination state rather than passed in:
	 * a public state has no session, so `getFlagsPrivate` would reject there.
	 *
	 * @param {Types.GetPresentationFlagsParams} params
	 * @returns {Promise} the feature flags payload
	 */
	getPresentationFlags(params) {
		const {trans} = params || {};
		if (!trans) {
			this.$log.error('"trans" is required to resolve presentation flags');
			return this.$q.reject(this.buildRejection({display: 'Error getting features flag'}));
		}

		const _toState = trans.to().name;
		const _isPublic = this.NavSvc.isStatePublic(_toState);
		const _flagParams = Object.assign({fromState: _toState}, trans.params());
		const _key = `${_isPublic ? 'public' : 'private'}:${JSON.stringify(_flagParams)}`;
		const _cached = this._presentationFlags;
		if (_cached && _cached.key === _key && Date.now() - _cached.iat < this.FLAG_CACHE_TTL_MS)
			return _cached.promise;

		const _promise = _isPublic ? this.getFlagsPublic(_flagParams) : this.getFlagsPrivate(_flagParams);
		this._presentationFlags = {key: _key, iat: Date.now(), promise: _promise};
		return _promise;
	}

	/** @param {Types.GetUserCapabilitiesParams} params */
	getUserCapabilities(params) {
		const _fromState = params?.fromState;
		if (!this.AuthSvc.hasValidUser()) {
			return this.$q.reject(this.buildRejection({display: this.NOT_LOGGED_IN, fromState: _fromState}));
		}

		const _user = this.AuthSvc.currentUser();
		const _display = 'Error getting user capabilities';
		const _header = {'X-State-Name': _fromState || ''};
		const _params = {serviceProviderId: _user.service_provider_id, userId: _user.id};
		return this.$http
			.get(`${this.api}/serviceProviderUser/capabilities`, {params: _params, headers: _header})
			.then(resObj => {
				const {data} = resObj;
				if (data?.success) {
					this.AuthSvc.setUserCapabilities(data.payLoad);
					return data.payLoad;
				}

				if (data?.error) {
					return this.$q.reject(
						this.buildRejection({display: _display, fromState: _fromState, error: data.error})
					);
				}

				return this.$q.reject(
					this.buildRejection({display: _display, fromState: _fromState, response: data})
				);
			})
			.catch(resObj => {
				this.$log.error(resObj);
				return this.$q.reject(this.buildRejection({display: _display, fromState: _fromState}));
			});
	}

	/** @param {Types.PostLastUserAccessParams} params */
	postLastUserAccess(params) {
		const _fromState = params?.fromState;
		if (!this.AuthSvc.hasValidUser()) {
			return this.$q.reject(this.buildRejection({display: this.NOT_LOGGED_IN, fromState: _fromState}));
		}

		const _user = this.AuthSvc.currentUser();
		const _display = 'Error logging last access';
		const _header = {'X-State-Name': _fromState || ''};
		// only log event with min interval of 60 min, else resolve silently
		const currentAccess = Date.now();
		const lastAccessed = this.StorageSvc.get(`last-accessed`);
		const minutesSince = this.dateFns.differenceInMinutes(currentAccess, lastAccessed);
		// NaN means the stored value is unusable, so treat it as never accessed. Testing
		// `minutesSince` for truthiness instead would make 0 log again and defeat the throttle.
		const _shouldLog = !lastAccessed || Number.isNaN(minutesSince) || minutesSince >= 60;
		if (!_shouldLog) return this.$q.resolve(minutesSince);

		const _params = {
			serviceProviderId: _user.service_provider_id,
			userId: _user.id,
		};
		return this.$http
			.post(`${this.api}/v1/serviceProviderUser/trackLastUserAccess`, _params, {headers: _header})
			.then(resObj => {
				const {data} = resObj;
				if (data?.success) {
					this.StorageSvc.set(`last-accessed`, currentAccess);
					return data.payLoad;
				} else if (data?.error) {
					return this.$q.reject(
						this.buildRejection({display: _display, fromState: _fromState, error: data.error})
					);
				} else {
					// neither flag set: settle anyway, a caller waiting on this forever is worse
					return this.$q.reject(
						this.buildRejection({display: _display, fromState: _fromState, response: data})
					);
				}
			})
			.catch(resObj => {
				this.$log.error(resObj);
				return this.$q.reject(this.buildRejection({display: _display, fromState: _fromState}));
			});
	}

	/** @param {Object} data */
	postLogAccess(data) {
		return this.MetricsSvc.log('view-presentation', data) || this.$q.resolve();
	}

	/**
	 * Rejects with a `{name, params}` redirect instruction
	 * Consumers do `.catch(state => $state.go(state.name, state.params))`.
	 * @param {Types.ValidateConsultationPrivateParams} params
	 * @returns {angular.IPromise<void>} resolves when status matches, else rejects with a
	 *   {@link Types.ConsultationRedirectRejection}
	 */
	validateConsultationPrivate(params) {
		// reading service_provider_id off a logged-out user would throw, so guard first
		if (!this.AuthSvc.hasValidUser()) return this.$q.reject({name: 'user.auth', params: {}});
		const _user = this.AuthSvc.currentUser();

		const _errMsg = {display: 'Consultation is in an unconsistent state or status'};
		return (
			this.ConsultationService.postValidateConsultationStateAndStatus({
				consultationId: params.consultationId,
				serviceProviderId: _user.service_provider_id,
				userId: _user.id,
			})
				.then(resObj => {
					/**
					 * @type {{success?: boolean, payLoad?: Types.ValidateConsultationPrivatePayload, error?: object}}
					 */
					const {data} = resObj;
					if (data?.success) {
						const {consultation_status_system_name: status} = data.payLoad;
						let toStateName;

						if (params.status === status) {
							return;
						} else {
							switch (status) {
								case 'lost':
									toStateName = 'preview.lost';
									Object.assign(params, {showBack: true});
									break;

								case 'presented':
									toStateName = 'preview.consultation';
									Object.assign(params, {showBack: true});
									break;

								case 'accepted':
									toStateName = 'preview.accepted';
									Object.assign(params, {showBack: true, proposalId: data.payLoad.proposal_id});
									break;

								case 'draft':
									toStateName = 'proposal.summary';
									break;

								default:
									toStateName = 'navigator';
									break;
							}
							// rejects with a redirect, not a display, so this log is the only record
							this.$log.error(`Status should match permission, value: ${params.status}, expected: ${status}`);

							return this.$q.reject({name: toStateName, params: params});
						}
					}

					if (data?.error) {
						this.$log.error(Object.assign({}, _errMsg, data.error));
						return this.$q.reject({name: 'navigator', params: params});
					}
				})
				// the rejection is not always an http response, so reach for .data defensively
				.catch(resObj => {
					// a redirect-shaped rejection (`{name, params}`) is the intended control flow
					if (resObj?.name) return this.$q.reject(resObj);

					this.$log.error(Object.assign({}, _errMsg, resObj?.data?.error));
					return this.$q.reject({name: 'navigator', params: params});
				})
		);
	}

	/**
	 * Rejects with a `{name, params}` redirect instruction
	 * Consumers do `.catch(state => $state.go(state.name, state.params))`.
	 *
	 * @param {Types.ValidateConsultationPublicParams} params
	 * @returns {angular.IPromise<void>} resolves when status matches and not expired, else
	 *   rejects with a {@link Types.ConsultationRedirectRejection}
	 */
	validateConsultationPublic(params) {
		const _errMsg = {display: 'Consultation is in an unconsistent state or status'};
		return (
			this.ConsultationService.getConsultationStateAndStatusForCustomer({
				serviceProviderId: params.serviceProviderId,
				customerToken: params.customerToken,
				consultationId: params.consultationId,
			})
				.then(resObj => {
					/**
					 * @type {{success?: boolean, payLoad?: Types.ValidateConsultationPublicPayload, error?: object}}
					 */
					const {data} = resObj;
					if (data?.success) {
						const {consultation_status_system_name: status, consultation_expired, proposal_id} = data.payLoad;

						if (params.status === status && !consultation_expired) {
							return;
						} else {
							let toStateName;
							switch (status) {
								case 'draft':
									toStateName = 'view.unavailable';
									break;
								case 'accepted':
									toStateName = 'view.accepted';
									Object.assign(params, {proposalId: proposal_id});
									break;
								case 'presented':
									toStateName = consultation_expired ? 'view.expired' : 'view.consultation';
									break;
								case 'lost':
									toStateName = 'view.lost';
									break;
								default:
									toStateName = 'view.unavailable';
									break;
							}
							// rejects with a redirect, not a display, so this log is the only record
							this.$log.error(
								`Status should match permission, value: ${params.status}, expected: ${status}, expired: ${consultation_expired}`
							);

							return this.$q.reject({name: toStateName, params: params});
						}
					}

					if (data?.error) {
						this.$log.error(Object.assign({}, _errMsg, data.error));
						return this.$q.reject({name: 'view.unavailable', params: params});
					}
				})
				// the rejection is not always an http response, so reach for .data defensively
				.catch(resObj => {
					// a redirect-shaped rejection (`{name, params}`) is the intended control
					if (resObj?.name) return this.$q.reject(resObj);

					this.$log.error(Object.assign({}, _errMsg, resObj?.data?.error));
					return this.$q.reject({name: 'view.unavailable', params: params});
				})
		);
	}
}
//
let svcMod = angular
	.module('mod.svc.RoutesResolve', [
		AuthSvcMod,
		ConsultationSvcMod,
		FeatureFlagsSvcMod,
		MetricsSvcMod,
		NavSvcMod,
		StorageSvcMod,
	])
	.service('RoutesResolveSvc', RoutesResolve);
export default (svcMod = svcMod.name);
